// ABOUTME: Global lightbox context for opening images from anywhere
// ABOUTME: Mount once in layout, use hook to trigger from any component

"use client"

import { createContext, useContext, useState, useCallback, ReactNode } from "react"
import { Lightbox } from "./lightbox"

export interface ExifData {
  camera?: string
  lens?: string
  focalLength?: string
  aperture?: string
  shutter?: string
  iso?: string
  date?: string
  location?: string
  artist?: string
  copyright?: string
}

export interface LightboxImage {
  src: string
  alt?: string
  exif?: ExifData
}

interface LightboxContextType {
  openLightbox: (images: LightboxImage[], startIndex?: number) => void
  closeLightbox: () => void
}

const LightboxContext = createContext<LightboxContextType | null>(null)

export function useLightbox() {
  const context = useContext(LightboxContext)
  if (!context) {
    throw new Error("useLightbox must be used within LightboxProvider")
  }
  return context
}

export function LightboxProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [images, setImages] = useState<LightboxImage[]>([])
  const [initialIndex, setInitialIndex] = useState(0)
  const [openCount, setOpenCount] = useState(0) // Used as key to force fresh mount

  const openLightbox = useCallback((newImages: LightboxImage[], startIndex = 0) => {
    setImages(newImages)
    setInitialIndex(startIndex)
    setOpenCount(c => c + 1) // Increment to force fresh Lightbox mount
    setIsOpen(true)
  }, [])

  const closeLightbox = useCallback(() => {
    setIsOpen(false)
  }, [])

  return (
    <LightboxContext.Provider value={{ openLightbox, closeLightbox }}>
      {children}
      <Lightbox
        key={openCount}
        images={images}
        initialIndex={initialIndex}
        isOpen={isOpen}
        onClose={closeLightbox}
      />
    </LightboxContext.Provider>
  )
}
